import { DateTime } from 'luxon'

/**
 * 数値を日本円の通貨形式（￥xxx,xxx,xxx）にフォーマットする
 *
 * @param value - フォーマットする数値
 * @returns フォーマットされた通貨文字列。値が null/undefined の場合は空文字列を返す
 *
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value !== 0 && !value) return ''
  return Number.parseInt(String(value || 0), 10).toLocaleString('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  })
}

/**
 * 数値をカンマ区切りの文字列にフォーマットする（通貨記号なし）
 *
 * @param value - フォーマットする数値
 * @returns フォーマットされた数値文字列。値が null/undefined の場合は空文字列を返す
 *
 */
export function formatNumber(value: number | null | undefined): string {
  if (value !== 0 && !value) return ''
  return Number.parseInt(String(value || 0), 10).toLocaleString('ja-JP')
}

/**
 * 値をluxon DateTimeに変換するヘルパー関数
 *
 * @param value - 変換する値（Date | string | number）
 * @returns 変換されたDateTime、無効な場合はnull
 */
export function toDateTime(value: Date | string | number): DateTime | null {
  let dt: DateTime

  if (value instanceof Date) {
    dt = DateTime.fromJSDate(value)
  } else if (typeof value === 'number') {
    // UNIXタイムスタンプ（秒）かミリ秒かを判定
    // 10桁以下は秒、それ以上はミリ秒として扱う
    if (value < 10000000000) {
      dt = DateTime.fromSeconds(value)
    } else {
      dt = DateTime.fromMillis(value)
    }
  } else {
    // 文字列の場合、ISO形式でパース
    dt = DateTime.fromISO(value)
  }

  return dt.isValid ? dt : null
}

/**
 * 日時を指定された形式でフォーマットする
 *
 * @param value - フォーマットする日時（Date | string | number）
 * @param format - フォーマット形式（'relative' | 'date' | 'datetime' | 'month' | 'salesMonth'）
 * @returns フォーマットされた日時文字列
 *
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  format: 'relative' | 'date' | 'datetime' | 'month' | 'salesMonth' = 'date',
): string {
  if (value == null || value === '') return '-'
  const dt = toDateTime(value)

  if (!dt) {
    return '-'
  }

  if (format === 'relative') {
    const now = DateTime.local()

    // カレンダー日ベースで判定（経過時間ではなく日付の差分で計算）
    if (dt.hasSame(now, 'day')) return '今日'

    const todayStart = now.startOf('day')
    const dtStart = dt.startOf('day')
    const diff = todayStart.diff(dtStart, ['years', 'months', 'days'])

    const diffDay = Math.floor(diff.days)
    const diffMonth = Math.floor(diff.months)
    const diffYear = Math.floor(diff.years)

    if (diffYear >= 1) return `${diffYear}年前`
    if (diffMonth >= 1) return `${diffMonth}ヶ月前`
    if (diffDay >= 1) return `${diffDay}日前`
    return '-'
  }

  if (format === 'datetime') {
    return dt.toFormat('yyyy/MM/dd HH:mm')
  }

  if (format === 'month') {
    return dt.toFormat('yyyy/MM')
  }

  if (format === 'salesMonth') {
    return dt.toFormat('yyyy年M月')
  }

  // format === 'date'
  return dt.toFormat('yyyy/MM/dd')
}
